<template>
  <div v-if="authChecking" class="signin-screen">
    <div class="signin-card">
      <span class="app-loader"></span>
      <p class="signin-sub">Loading…</p>
    </div>
  </div>
  <div v-else-if="!logged" class="signin-screen">
    <div class="signin-card">
      <img class="signin-logo" src="/favicon.svg" alt="Prody Block" />
      <div class="signin-brand">Prody Block</div>
      <h1 class="signin-title">
        {{ expired ? "Welcome back" : "Sign in to Prody Block" }}
      </h1>
      <p class="signin-sub">
        <template v-if="expired">
          You were signed out while working. Sign in again — anything you had
          not saved is still here and comes back with you.
        </template>
        <template v-else>
          Signing in uses your Prodyflow account — the same login you already
          have.
        </template>
      </p>

      <button
        class="signin-btn"
        :class="{ 'is-busy': connecting }"
        :disabled="!managerUrl"
        v-on:click="loginViaPopup"
      >
        <span class="app-loader small on-btn" v-if="connecting"></span>
        {{ connecting ? "Signing in…" : "Sign in with Prodyflow" }}
      </button>

      <div class="signin-note" v-if="!managerUrl">
        Sign-in is temporarily unavailable. Please contact your administrator.
      </div>
      <div class="signin-note" v-else-if="needsManagerLogin">
        No login was found in Prodyflow. Sign in to
        <a :href="managerUrl" target="_blank">Prodyflow</a> in another tab
        first, then click the button above again.
      </div>
      <div class="signin-note" v-else-if="slow && !expired">
        Still connecting. Please sign in to Prodyflow in another tab, then click
        the button above again.
      </div>
    </div>
  </div>
  <router-view />
  <iframe
    style="display: none"
    ref="managerIframe"
    :src="managerUrl + '/loginEditor.html'"
    sandbox="allow-scripts allow-same-origin"
  ></iframe>
</template>
<script>
import store from "@/store";
import http from "./modules/http";
import tenant from "./modules/tenant";
import i18n from "./i18n";

// Kept outside the component: a cross-origin WindowProxy must not go through
// Vue's reactivity (reading its properties throws a SecurityError).
let popupWindow = null;

export default {
  name: "AppStart",
  data() {
    return {
      logged: false,
      authChecking: true,
      slow: false,
      expired: false,
      connecting: false,
      needsManagerLogin: false,
      tokenInterval: null,
      slowTimeout: null,
      popupInterval: null,
    };
  },
  computed: {
    managerUrl() {
      return process.env.VUE_APP_MANAGER_URL;
    },
    apiUrl() {
      return tenant.getApiBaseUrl();
    },
  },
  mounted() {
    const _this = this;
    window.addEventListener(
      "message",
      function (event) {
        const { action, token } = event.data;
        if (action === "setToken") {
          if (!token || token.length < 1) {
            // The manager had no token to hand over — the user is not signed in
            // there. The old code navigated the whole editor away to the
            // manager here, which yanked it out from under the user and read as
            // "it won't log in". Stay on our own sign-in screen instead and say
            // what to do.
            _this.connecting = false;
            _this.needsManagerLogin = true;
            return;
          }
          _this.needsManagerLogin = false;
          _this.applyToken(token);
        }
      },
      false
    );

    // A 401 anywhere in the app lands here. Re-running the manager handshake
    // usually succeeds without the user noticing, because the manager's own
    // session is still alive; only if that fails do they see the sign-in
    // screen — and they stay on the page they were on.
    window.addEventListener("editor-auth-expired", () => {
      if (!_this.logged) {
        return;
      }
      _this.logged = false;
      _this.expired = true;
      localStorage.removeItem("editor_token");
      _this.getToken();
      _this.tokenInterval = setInterval(_this.getToken, 2000);
    });

    // A page reload should not need the manager again: try the cached token.
    const cached = localStorage.getItem("editor_token");
    if (cached) {
      this.tryCachedToken(cached);
    }

    this.getToken();
    // keep asking the manager iframe until a token arrives (it may load slowly)
    this.tokenInterval = setInterval(this.getToken, 2000);
    this.slowTimeout = setTimeout(() => {
      this.slow = true;
      this.authChecking = false;
    }, 8000);
  },
  unmounted() {
    this.stopPolling();
  },
  methods: {
    applyToken(token) {
      if (this.logged) {
        return;
      }
      store.commit("login", {
        token: token,
      });

      http.fetch("/users/me").then((data) => {
        store.commit("login", {
          token: token,
          user: data,
        });
        i18n.global.locale.value = (
          data.language_code ||
          navigator.language ||
          "en"
        ).slice(0, 2);
        localStorage.setItem("editor_token", token);
        this.logged = true;
        this.authChecking = false;
        this.expired = false;
        this.connecting = false;
        this.stopPolling();
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
        popupWindow = null;
      });
    },
    tryCachedToken(token) {
      // Validate with a raw fetch so an expired token is dropped silently
      // instead of redirecting to the manager.
      fetch(tenant.getApiBaseUrl() + "/users/me", {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("invalid token");
          }
          return res.json();
        })
        .then((user) => {
          store.commit("login", {
            token: token,
            user: user,
          });
          i18n.global.locale.value = (
            user.language_code ||
            navigator.language ||
            "en"
          ).slice(0, 2);
          this.logged = true;
          this.authChecking = false;
          this.stopPolling();
        })
        .catch(() => {
          localStorage.removeItem("editor_token");
          this.authChecking = false;
        });
    },
    // The hidden-iframe flow breaks on localhost: Chrome partitions
    // third-party iframe storage, so the manager iframe sees an empty
    // localStorage. A popup is a top-level window, sees the real login,
    // and can postMessage the token back to us.
    loginViaPopup() {
      if (this.logged || !this.managerUrl) {
        return;
      }
      this.connecting = true;
      popupWindow = window.open(
        this.managerUrl + "/loginEditor.html",
        "editorLogin",
        "width=420,height=280"
      );
      if (!popupWindow) {
        // popup blocked — do not leave the button spinning forever
        this.connecting = false;
        return;
      }
      if (this.popupInterval) {
        clearInterval(this.popupInterval);
      }
      this.popupInterval = setInterval(() => {
        if (this.logged || popupWindow === null || popupWindow.closed) {
          clearInterval(this.popupInterval);
          this.popupInterval = null;
          this.connecting = false;
          return;
        }
        popupWindow.postMessage({ action: "getToken" }, "*");
      }, 700);
    },
    stopPolling() {
      if (this.tokenInterval) {
        clearInterval(this.tokenInterval);
        this.tokenInterval = null;
      }
      if (this.popupInterval) {
        clearInterval(this.popupInterval);
        this.popupInterval = null;
      }
      if (this.slowTimeout) {
        clearTimeout(this.slowTimeout);
        this.slowTimeout = null;
      }
    },
    getToken() {
      if (this.logged || !this.$refs.managerIframe?.contentWindow) {
        return;
      }
      this.$refs.managerIframe.contentWindow.postMessage(
        {
          action: "getToken",
        },
        "*"
      );
    },
  },
};
</script>
